import { useEffect } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { useInjection } from 'inversify-react';
import { type RolesService, RolesServiceIdentifier } from '@shared/types/mobx/services/roles';
import { hasSessionToken } from '@shared/api/session';
import { getSelectableRoles } from '@shared/utils/helpers/selectableRoles';
import LoadingScreen from '@shared/components/LoadingScreen';
import { PATH_AUTH } from '../paths';
import { resolveRoleGuardDecision } from './roleGuardDecision';

/**
 * Гвард закрытых зон: «роль выбрана?» (дефект «сброс роли на ABONENT после очистки сессии»).
 *
 * ПРОБЛЕМА:
 * Любая очистка сессии («Выйти» или любой 401 → `clearAllSessions()` в mobx/root.ts) обнуляет
 * `RolesModule.currentRole` и сбрасывает `ProfileModule.userData` к `initialUser`, где
 * `currentRole` — заглушка `{ role: 'ABONENT', nameRu: '' }` (mobx/initial). После входа
 * приложение ведёт на `/auth/select-role`, но если роль там не выбрали (открыли /cabinet по
 * закладке, восстановили вкладку, нажали «Назад») — AuthGuard пропускал: он проверяет только
 * токен. ProfileModule переносит роль из RolesModule лишь когда она не null, поэтому в профиле
 * оставалась заглушка ABONENT, и председатель видел кабинет абонента (нет «Мои дома»).
 *
 * СОСТОЯНИЯ (порядок проверок важен, вся логика — в resolveRoleGuardDecision):
 * 1. Токена нет — мы внутри logout()/sessionExpired(), они сами уводят со страницы.
 *    Показываем лоадер и НЕ редиректим, иначе две навигации подерутся.
 * 2. Persist-стор RolesModule ещё не гидратирован — `currentRole` пуст у всех. Лоадер.
 * 3. Роль выбрана — пропускаем, всё как раньше.
 * 4. Доступная для выбора роль ровно одна — выбираем её автоматически тем же путём, что экран
 *    выбора (`rolesModule.selectRole`), но с `withRedirect = false`: пользователь остаётся на
 *    открытой странице. Выбор делаем в useEffect (менять стор во время рендера нельзя), до этого
 *    — лоадер; после выбора гвард перерисуется (observer) и перейдёт в состояние 3.
 * 5. Доступных ролей больше одной — на `/auth/select-role`. `replace` обязателен: иначе «Назад»
 *    вернёт на закрытую страницу и всё повторится (цикл в истории).
 * 6. Доступных ролей нет — пропускаем (как было до гварда), см. комментарий в roleGuardDecision.
 *
 * ЦИКЛА РЕДИРЕКТОВ НЕТ: `/auth/*` (в т.ч. select-role) лежит ВНЕ авторизованного периметра
 * (routesConfig.tsx), а экран выбора роли уводит в /cabinet только после выбора, т.е. уже с
 * непустым currentRole.
 *
 * ПОЧЕМУ RolesModule, А НЕ ProfileModule.userData.currentRole: в профиле после очистки сессии
 * лежит заглушка ABONENT, отличить её от реально выбранной роли «Абонент» нельзя. Источник
 * правды о выборе — `RolesModule.currentRole` (null = не выбрана).
 */
export const RoleSelectedGuard = observer(() => {
  const rolesModule = useInjection<RolesService>(RolesServiceIdentifier);

  const decision = resolveRoleGuardDecision({
    hasToken: hasSessionToken(),
    isStoreHydrated: rolesModule.isStoreHydrated,
    currentRole: rolesModule.currentRole,
    selectableRoles: getSelectableRoles(rolesModule.roles)
  });

  const autoSelectRole = decision.type === 'autoSelect' ? decision.role : null;

  // (4) Автовыбор единственной роли — побочный эффект, поэтому не в рендере.
  useEffect(() => {
    if (autoSelectRole) rolesModule.selectRole(autoSelectRole, false);
  }, [autoSelectRole, rolesModule]);

  // (5) Ролей несколько, ни одна не выбрана — на экран выбора роли.
  if (decision.type === 'redirect') return <Navigate to={PATH_AUTH.selectRole} replace />;

  // (3), (6) Роль выбрана / выбирать нечего — раздел работает как раньше.
  if (decision.type === 'pass') return <Outlet />;

  // (1), (2), (4) — ждём гидрации / навигации logout'а / применения автовыбора.
  return <LoadingScreen />;
});

export default RoleSelectedGuard;
